import { useState, useEffect, useRef } from "react";
import { Search, ChevronDown, Check } from "lucide-react";
import { Input } from "./ui/input";
import { cn } from "../lib/utils";

/**
 * Componente Select generico con ricerca
 */
const SearchableSelect = ({ 
  items = [], 
  value, 
  onChange, 
  placeholder = "Seleziona...",
  searchPlaceholder = "Cerca...",
  disabled = false,
  renderItem = null, // Funzione per renderizzare ogni item: (item) => string | JSX
  getItemLabel = (item) => item.nome || item.label || item.name || "", // Come ottenere il label per display
  getItemValue = (item) => item.id || item.value, // Come ottenere il value
  getItemSubtext = null, // Opzionale: (item) => string per testo secondario
  icon = null, // Icona opzionale da mostrare
  emptyMessage = "Nessun elemento trovato"
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  
  // Chiudi dropdown quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filtra items per ricerca
  const itemsFiltered = items.filter(item => {
    const label = getItemLabel(item);
    return label?.toLowerCase().includes(search.toLowerCase());
  });

  // Ordina alfabeticamente
  const sortedItems = [...itemsFiltered].sort((a, b) => 
    (getItemLabel(a) || '').localeCompare(getItemLabel(b) || '', 'it')
  );

  // Trova item selezionato
  const selectedItem = items.find(item => getItemValue(item) === value);
  
  // Display label
  const displayLabel = selectedItem ? getItemLabel(selectedItem) : placeholder;

  const handleSelect = (item) => {
    onChange(getItemValue(item), item);
    setOpen(false);
    setSearch("");
  };

  const IconComponent = icon;

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        className={cn(
          "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background text-left",
          "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          !value && "text-muted-foreground"
        )}
      >
        <span className="truncate flex items-center gap-2">
          {IconComponent && selectedItem && <IconComponent className="h-4 w-4 shrink-0" />}
          {displayLabel}
        </span>
        <ChevronDown className={cn("h-4 w-4 opacity-50 transition-transform flex-shrink-0", open && "rotate-180")} />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-lg">
          {/* Search input */}
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={searchPlaceholder}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-9"
                autoFocus
              />
            </div>
          </div>

          {/* Lista items */}
          <div className="max-h-[300px] overflow-y-auto p-1">
            {sortedItems.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                {emptyMessage}
              </div>
            ) : (
              sortedItems.map(item => {
                const itemValue = getItemValue(item);
                const isSelected = value === itemValue;
                
                return (
                  <button
                    key={itemValue}
                    onClick={() => handleSelect(item)}
                    className={cn(
                      "flex w-full items-center justify-between px-3 py-2 text-sm rounded-sm cursor-pointer",
                      "hover:bg-accent hover:text-accent-foreground transition-colors",
                      isSelected && "bg-accent"
                    )}
                  >
                    <span className="truncate flex items-center gap-2">
                      {IconComponent && <IconComponent className="h-4 w-4 shrink-0 opacity-60" />}
                      {renderItem ? renderItem(item) : getItemLabel(item)}
                    </span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {getItemSubtext && (
                        <span className="text-xs text-muted-foreground">
                          {getItemSubtext(item)}
                        </span>
                      )}
                      {isSelected && <Check className="h-4 w-4" />}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
